import React, { JSX, useContext, useEffect, useRef } from "react";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { UploadBulkExceptionsResponse } from "@app/exceptions/interfaces";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardStepProps } from "@app/exceptions/contexts/interfaces";
import { UploadExceptionsContext } from "@app/exceptions/contexts/UploadExceptionsContext";
import ExceptionsService from "@app/exceptions/exceptionsService";

type InvalidRecordKey =
  | 'comments'
  | 'countySSA'
  | 'duplicateRecords'
  | 'exceptionStatus'
  | 'expirationDate'
  | 'grade'
  | 'invalidDatesCombination'
  | 'narrative'
  | 'previouslyApproved'
  | 'projectName'
  | 'reviewStatus'
  | 'specialtyGroupCode'
  | 'startDate';

type InvalidRecord = Record<InvalidRecordKey, { description: string; count: number }>;

const UploadExceptionsImport = (props: WizardStepProps): JSX.Element => {
  const uploadExceptionsContext = useContext(UploadExceptionsContext);
  const templateFileUri = useRef<string>(`${window.apiEndpointV2_0}/specialtygroupexceptions/file/upload-bulk-template`).current;
  const uploadObjectKey = "file";

  const exceptionService = new ExceptionsService();

  useEffect(() => {
    props.pageIsValid(!uploadExceptionsContext.loading);
  }, [uploadExceptionsContext.loading]);

  const downloadTemplateFile = () => {
    authenticatedDownload(templateFileUri, undefined, new URLSearchParams("populateProjectNames=true"));
  }

  const initializeInvalidRecords = (): InvalidRecord => {
    return {
      'comments': {
        count: 0, description: 'records were not updated due to invalid Comments'
      },
      'countySSA': {
        count: 0, description: 'records were not updated due to missing or invalid County SSA'
      },
      'duplicateRecords': {
        count: 0, description: 'duplicate records were found and only the first valid record will be used.'
      },
      'exceptionStatus': {
        count: 0, description: 'records were not updated due to missing or invalid Exception Status'
      },
      'expirationDate': {
        count: 0, description: 'records were not updated due to invalid Expiration Date'
      },
      'grade': {
        count: 0, description: 'records were not updated due to invalid Exception Grade'
      },
      'invalidDatesCombination': {
        count: 0, description: 'records were not updated due to Start Date being after Expiration Date'
      },
      'narrative': {
        count: 0, description: 'records were not updated due to invalid Exception Narrative'
      },
      'previouslyApproved': {
        count: 0, description: 'records were not updated due to invalid Previously Approved'
      },
      'projectName': {
        count: 0, description: 'records were not updated due to missing or invalid Project Name'
      },
      'reviewStatus': {
        count: 0, description: 'records were not updated due to invalid Internal Review Status'
      },
      'specialtyGroupCode': {
        count: 0, description: 'records were not updated due to missing or invalid Specialty Group Code'
      },
      'startDate': {
        count: 0, description: 'records were not updated due to invalid Start Date'
      }
    };
  };

  const loadInvalidRecords = (response: UploadBulkExceptionsResponse, invalidRecords: InvalidRecord) => {
    Object.entries(response.invalidRecords).map(([key, value]) => {
      switch (key) {
        case 'ProjectNameRequired':
        case 'ProjectNameDoesNotExist':
        case 'ProjectNameDuplicate':
          invalidRecords['projectName'].count += value;
          break;
        case 'SpecialtyGroupCodeRequired':
        case 'SpecialtyGroupCodeDoesNotExist':
          invalidRecords['specialtyGroupCode'].count += value;
          break;
        case 'CountySSARequired':
        case 'CountySSADoesNotExist':
          invalidRecords['countySSA'].count += value;
          break;
        case 'ExceptionStatusRequired':
        case 'InvalidExceptionStatus':
          invalidRecords['exceptionStatus'].count += value;
          break;
        case 'CommentExceedsMaxLength':
          invalidRecords['comments'].count += value;
          break;
        case 'ExceptionNarrativeExceedsMaxLength':
          invalidRecords['narrative'].count += value;
          break;
        case 'InvalidStartDateFormat':
          invalidRecords['startDate'].count += value;
          break;
        case 'InvalidEndDateFormat':
          invalidRecords['expirationDate'].count += value;
          break;
        case 'InvalidPreviouslyApprovedValue':
          invalidRecords['previouslyApproved'].count += value;
          break;
        case 'StartDateGreaterThanEndDate':
          invalidRecords['invalidDatesCombination'].count += value;
          break;
        case 'DuplicateRecordsCount':
          invalidRecords['duplicateRecords'].count += value;
          break;
        case 'InvalidReviewStatusValue':
          invalidRecords['reviewStatus'].count += value;
          break;
        case 'InvalidGradeValue':
          invalidRecords['grade'].count += value;
      }
    });
  }

  const getUniqueCustomProjectIds = (validRecords: { customProjectId: number }[]): number => {
    const uniqueProjectIds = new Set<number>();

    validRecords.forEach(record => {
      uniqueProjectIds.add(record.customProjectId);
    });

    return uniqueProjectIds.size;
  }

  const renderInvalidRecords = (data: InvalidRecord): JSX.Element => {
    return (
      <>
        {Object.entries(data)
          .filter(([, value]) => value.count > 0)
          .map(([key, value]) => {
            const description = value.count === 1 ? value.description.replace('records were', 'record was') : value.description;
            return (
              <React.Fragment key={key}>
                <span key={`${key}`}>{`${value.count} ${description}`}</span><br />
              </React.Fragment>
            );
          })}
      </>
    );
  };

  const handleFileUpload = async (data: FormData) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const response = await exceptionService.uploadBulkExceptionsFile(data);

        const invalidRecords: InvalidRecord = initializeInvalidRecords();
        loadInvalidRecords(response, invalidRecords);

        const invalidItems: JSX.Element = renderInvalidRecords(invalidRecords);

        const uniqueProjectIdCount = getUniqueCustomProjectIds(response.validRecords);

        const totalRecordsCount = response.totalRecords === 1
          ? '1 Total Record'
          : `${response.totalRecords} Total Records`;
        const validRecordsCount = response.validRecords.length === 1
          ? '1 Valid Exception Record'
          : `${response.validRecords.length} Valid Exception Records`;
        const uniqueProjectsCount = uniqueProjectIdCount === 1
          ? '1 Project will be Updated'
          : `${uniqueProjectIdCount} Projects will be Updated`;

        const validItems: JSX.Element = (
          <>
            <span>{totalRecordsCount}</span><br />
            <span>{validRecordsCount}</span><br />
            <span>{uniqueProjectsCount}</span><br />
          </>
        );

        uploadExceptionsContext.setInvalidItems([{ invalidItems, [wafflesKeyColumnName]: "key" }]);
        uploadExceptionsContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);
        uploadExceptionsContext.setValidRecords(response.validRecords);
        uploadExceptionsContext.setTotalOverwrittenExceptions(response.totalOverwrittenExceptions);
        uploadExceptionsContext.setBulkUploadFormData(data);

        resolve();
      } catch (error) {
        reject(error.responseJSON);
      }
    });
  };

  useEffect(() => {
    props.pageIsValid(!!uploadExceptionsContext.bulkUploadFormData);
  }, [uploadExceptionsContext.bulkUploadFormData]);

  return (
    <PageSection>
      <div className="mt-3 col">
        <div className="row">
          <div className="col-6">
            <p>Select the file you want to upload. The data needs to be in a .csv file. &nbsp;
              <QESButton qesType="secondary" className="bg-transparent fw-normal p-0" qesTestId="upload-exceptions__templalte-file" onClick={downloadTemplateFile}>
                Download
              </QESButton>
              &nbsp; a template that includes a list of your current projects.
            </p>
            <p className="demibold">Files <u>must</u> include columns with values for:</p>
            <ul style={{ marginTop: "-15px" }}>
              <li>Project</li>
              <li>Service Area - County SSA (Value Ex. 43209. Leading zeros <strong>MUST</strong> be included in the file upload.)</li>
              <li>Specialty Group Code (value Ex. 022. Leading zeros <strong>MUST</strong> be included in the file upload)</li>
              <li>Exception Status (Values: Approved, Pending, Submitted, Denied, Appealed, Expired)</li>
            </ul>
            <p className="demibold">Optional Columns with values may include:</p>
            <ul style={{ marginTop: "-15px" }}>
              <li>Internal Review Status (Not Ready, Ready for Review, Ready for Submission)</li>
              <li>Start Date (Value Ex. 01/01/2025, 1/1/25)</li>
              <li>Expiration Date (Value Ex. 12/31/2025, 1/1/25)</li>
              <li>Exception Grade (A, B, C)</li>
              <li>Previously Approved (Value Ex. Y/N, Yes/No, True/False, T/F)</li>
              <li>Comments (for Internal Use Only) (Maximum 4000 characters)</li>
              <li>Exception Narrative (Maximum 4000 characters)</li>
            </ul>
            <DragAndDropFileUploader
              apiKeyName={uploadObjectKey}
              filetype=".csv"
              hasBeenSuccessful={!!uploadExceptionsContext.bulkUploadFormData}
              onUpload={handleFileUpload}
              testId="upload-exceptions__file-upload"
            />
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default UploadExceptionsImport;
